import { FormData as CandidateFormData } from './form-controls';

export const URL_PREFIXES = {
  linkedinUrl: 'https://www.linkedin.com/in/',
  githubUrl: 'https://github.com/',
  googleScholarUrl: 'https://scholar.google.com/citations?user=',
} as const;

export type SocialField = keyof typeof URL_PREFIXES;

export const isValidEmail = (email: string): boolean => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email).toLowerCase());
};

const isParsableUrl = (value: string): boolean => {
  try {
    return Boolean(new URL(value));
  } catch {
    return false;
  }
};

// Build the full profile URL from a handle. The backend validates these with @IsUrl, which rejects
// spaces and malformed values with an opaque 400 — so reject them here with a clear message instead.
// Empty is allowed (required-ness is enforced separately in validateCandidateForm).
export function buildSocialUrl(
  field: SocialField,
  rawValue: string,
): { url?: string; error?: string } {
  const value = (rawValue || '').trim();
  if (!value) return {};
  if (/\s/.test(value)) {
    return { error: 'Enter just the username/handle — no spaces or full URLs.' };
  }
  const url = `${URL_PREFIXES[field]}${value}`;
  if (!isParsableUrl(url)) {
    return { error: 'This doesn’t look like a valid profile link.' };
  }
  return { url };
}

export function validateCandidateForm(params: {
  formData: CandidateFormData;
  hasResume: boolean;
  phoneDigits: number;
}): Record<string, string> {
  const { formData, hasResume, phoneDigits } = params;
  const errors: Record<string, string> = {};

  if (!hasResume) errors.resume = 'Resume is required';
  if (!formData.name) errors.name = 'Name is required';
  if (!formData.email) errors.email = 'Email is required';
  else if (!isValidEmail(formData.email)) errors.email = 'Please enter a valid email address.';
  if (!formData.phone || phoneDigits === 0) errors.phone = 'Phone number is required';
  if (!formData.yearsOfExperience) errors.yearsOfExperience = 'Years of experience is required';
  if (!formData.countryId.length) errors.countryId = 'Country is required';
  if (!formData.developerType.length) errors.developerType = 'Developer type is required';
  if (!formData.expectedSalary) errors.expectedSalary = 'Expected salary is required';
  if (!formData.linkedinUrl) errors.linkedinUrl = 'LinkedIn username is required';

  (Object.keys(URL_PREFIXES) as SocialField[]).forEach((field) => {
    if (errors[field]) return;
    const { error } = buildSocialUrl(field, formData[field] as string);
    if (error) errors[field] = error;
  });

  return errors;
}
